// Hook: liefert ein Höhenprofil für einen Trail.
//
// Drei Fälle:
//   1. Trail hat genug `ele` in seinen GPX-Punkten → wir bauen Samples direkt
//      aus den Punkten (downsampled auf ~100 Samples für glattes Rendering).
//   2. Trail hat KEINE Höhen → wir prüfen IndexedDB-Cache ('trailElevations').
//      Cache-Hit → direkt benutzen.
//   3. Cache-Miss → map.queryTerrainElevation an 100 Sample-Punkten, in
//      IndexedDB speichern, returnen.
//
// Für Fall 3 brauchen wir:
//   a) die MapLibre-Map (für queryTerrainElevation) — aus mapBus
//   b) ein Style der Terrain-Tiles hat (mapout hat, minimal/satellit nicht)
//   c) Terrain setTerrain aktiviert (passiert in MapView.enableSilentTerrain)
//
// Wenn irgendwas davon fehlt, returned der Hook {samples: null} und das
// ElevationProfile-Widget rendert halt nix.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { GpxPoint, GpxTrack } from '@adtu/shared';
import type { Map as MLMap } from 'maplibre-gl';
import { trackLength } from '@adtu/shared';
import {
  buildSrtmElevationSamples,
  cumulativeDistances,
  hasElevationData,
  sampleDistances,
  clampElevationByGrade,
  despikeElevationSamples,
  smoothElevationSamples,
} from './trailGeom';
import { getDb } from './storage/db';
import { mapBus } from './map/mapBus';
import { fetchHttpElevations } from './httpElevation';
import { enqueueElevationPush } from './elevationQueue';
import {
  isOfflineDemAvailable,
  sampleManyPmtilesElevations,
} from './dem/pmtilesElevationSampler';
import { isEverestEasterEggId } from './everestEasterEgg';

/**
 * Min/Max lon/lat der Track-Punkte als MapLibre-Bbox-Tuple.
 * Wave 14as: Für die fitBounds-vor-Rebuild-Choreographie, damit die DEM-Tiles
 * für den gesamten Trail vorgewärmt werden bevor wir samplen.
 */
function trackBbox(
  points: GpxPoint[],
): [[number, number], [number, number]] | null {
  if (points.length < 2) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
    if (p.lon > maxLon) maxLon = p.lon;
  }
  return [
    [minLon, minLat],
    [maxLon, maxLat],
  ];
}

/**
 * Wartet auf das nächste `'idle'`-Event (keine laufenden Tile-Requests mehr)
 * oder auf Timeout. MapLibre feuert 'idle' auch für DEM-Sources — so wissen
 * wir, dass alle DEM-Tiles für die aktuelle Viewport tatsächlich geladen
 * sind, und nicht nur angefordert. 3.5s ist grosszügig genug für AWS-Terrarium-
 * Fallback auf mobilem Handynetz.
 */
function waitForMapIdle(map: MLMap, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      try {
        map.off('idle', finish);
      } catch {
        /* map may already be unmounted */
      }
      resolve();
    };
    map.on('idle', finish);
    window.setTimeout(finish, timeoutMs);
  });
}

export interface TrailElevationData {
  /** Höhen-Samples in Metern. `null` wenn weder GPS noch SRTM verfügbar. */
  elevations: number[] | null;
  /** Distanzen in Metern, parallel-indiziert zu `elevations`. */
  distances: number[];
  /** Gesamt-Track-Länge in Metern. */
  totalMeters: number;
  /** Woher die Daten kommen — nur für UI-Debug. */
  source: 'gpx' | 'srtm' | 'none' | 'pending';
  /**
   * Zwingt einen frischen SRTM-Fetch: löscht IDB-Cache für diesen Trail,
   * setzt source='pending', und triggert den kompletten Retry-Loop neu.
   * Nützlich für den "Rebuild elevation"-Button, der bei flach-bleibenden
   * Profilen eine manuelle Wieder-Sampling-Runde startet (z.B. wenn der
   * erste Lauf mit nicht-geladenen DEM-Tiles in einen 523m-Flatline
   * gelaufen ist).
   *
   * No-op wenn trailId/track null sind.
   */
  rebuild: () => Promise<void>;
}

// Wave 14cf-fix3 — distance-aware Sampling.
//
// Vorher: SAMPLE_COUNT = 100 fix, egal wie lang der Track. Auf einer
// 73 km-Tour bedeutete das ~730 m zwischen Samples — alle Switchback-
// Climbs (50–200 m horizontal, oft 50–100 m Höhe) verschwinden zwischen
// zwei Samples. User-Report: "73 km Custom Route, Garmin/Apple Watch/
// Veranstalter sagen 2600 m HM, App zeigt 563 m HM" → das ist genau
// dieser Bug, kein Smoothing-Issue allein.
//
// Jetzt: Wir zielen auf ~50 m Sample-Abstand. Floor + Ceiling damit:
//   - kurze Trails nicht unter-sampelt sind (MIN_SAMPLES = 100, Sweet-
//     Spot für glatte SVG-Linien)
//   - sehr lange Strecken nicht ausarten (MAX_SAMPLES = 2000, ~100 km
//     bei 50 m Abstand; bei längeren Touren wird die Auflösung leicht
//     gröber, aber 50 m * 2000 = 100 km Reichweite ist mehr als jede
//     reale Bike-Tour braucht).
const TARGET_SAMPLE_SPACING_METERS = 25;
const MIN_SAMPLES = 100;
const MAX_SAMPLES = 2000;

function pickSampleCount(totalMeters: number): number {
  if (!Number.isFinite(totalMeters) || totalMeters <= 0) return MIN_SAMPLES;
  const ideal = Math.round(totalMeters / TARGET_SAMPLE_SPACING_METERS);
  return Math.max(MIN_SAMPLES, Math.min(MAX_SAMPLES, ideal));
}

/**
 * Wave 14bk: Smoothing-Pipeline-Version. Jede Änderung am Smoothing-
 * Algorithmus (Window-Size, Filter-Typ, Deadband etc.) muss diese Version
 * inkrementieren — Cache-Einträge mit anderer Version werden in
 * loadCachedSrtm verworfen, damit alle Trails den neuen Algorithmus
 * rerunnen. Start bei 1 (pre-14bk hatte gar kein Feld → undefined ≠ 1).
 *
 * Wave 14ce: Hochgesetzt auf 2. smoothElevationSamples macht jetzt
 * SYMMETRISCHES Edge-Shrinking — Endpunkte (Start/Gipfel) bleiben exakt
 * auf raw-Wert.
 *
 * Wave 14cf-fix3: Hochgesetzt auf 3. Sample-Count + Smoothing-Window sind
 * jetzt distanzbasiert (siehe pickSampleCount / pickSmoothingWindow); alte
 * 100-Sample-Caches mit 9-Sample-Smoothing (= ~6.5 km Window auf 73 km,
 * killt jede Switchback) sind unbrauchbar.
 *
 * Wave 14cf-fix4: Hochgesetzt auf 4. SMOOTHING_WINDOW_METERS von 60 → 150
 * angehoben, weil fix3 zwar das alte Under-Counting-Problem gelöst hat,
 * aber dadurch eine neue Klasse von Phantom-Gain auf langen Strecken
 * eingeführt: SRTM-Cell-Übergangs-Noise (~3–5 m typisch) summierte sich
 * bei 25 m Sample-Spacing + 60 m (= 3 Samples) Smoothing über 2000 Samples
 * zu mehreren hundert Metern. User-Report (Bilder 1791/1792): "51 km Elba-
 * Tour, real 973 m HM, App zeigt 2110 m HM, Profil sieht chaotisch/nervös
 * aus". 150 m Window = 6 Samples bei 25 m Spacing, drückt das Phantom auf
 * <50 m bei realistischem ±5 m Noise (vs ~190 m mit 60 m Window). Reale
 * Climbs ≥ ~250 m breit bleiben präzise, schmalere Switchbacks (10 m
 * vertikal über 150 m horizontal) verlieren noch ~10 % ihrer Höhe — ok-
 * Tradeoff bei einem 51 km Track wo Genauigkeit der Headline wichtiger
 * ist als jeder Mini-Kicker.
 *
 * Wave 14cc-fix5: Hochgesetzt auf 5. Doppelter Grund:
 *   (a) neue Pipeline-Ordnung (Online-HTTP first, Offline-PMTiles als
 *       Fallback) — alle Cache-Einträge aus der alten "PMTiles-first"-
 *       Welt enthalten potenziell korrupte Höhenwerte aus dem Liguria-
 *       DEM-Source (Melogno-Buckel) und müssen verworfen werden.
 *   (b) HTTP-Sampler-Cap auf 100 Samples (httpElevation.ts:
 *       HTTP_PROVIDER_MAX_SAMPLES) — vorher scheiterten die Provider
 *       still bei >100 Samples, was die Korruption im Offline-Fallback
 *       gemaskt hat. Mit dem Fix klappt HTTP wieder, alte Caches enthalten
 *       die fehlerhaften Offline-Werte und müssen weg.
 */
const SMOOTHING_VERSION = 5;

// Smoothing-Window in METERN, nicht in Samples. Das macht's track-
// längen-unabhängig: ein 150 m Moving-Average glättet SRTM-Cell-
// Übergangs-Noise (~3–5 m pro Sample bei 25 m Spacing) ohne breite
// Climbs (≥ 250 m) wegzudrücken.
//
// Tuning-Historie:
//   - 80 m (fix3-vorab): zu wenig Smoothing, GPS/SRTM-Noise sichtbar
//   - 60 m (fix3): I26-Test 73 km/2600 m sehr nah am Ziel (-4 %), aber
//     Phantom-Gain auf real-flachem ±5 m-Noise: 191 m über 73 km
//   - 150 m (fix4 — hier): Phantom auf 38 m gedrückt, I26 noch -10 %
//     vom Ziel. Realer User-Track Elba 51 km/973 m wird damit ~1000–
//     1100 m statt der bisher gemeldeten 2110 m.
//
// Test-Suite: outputs/elev_grid_search.mjs
const SMOOTHING_WINDOW_METERS = 150;

function pickSmoothingWindow(
  totalMeters: number,
  sampleCount: number,
): number {
  if (sampleCount < 3 || totalMeters <= 0) return 3;
  const sampleSpacing = totalMeters / Math.max(1, sampleCount - 1);
  const idealWindow = Math.round(SMOOTHING_WINDOW_METERS / sampleSpacing);
  // Untergrenze 3 (sonst kein Smoothing, GPS-Noise bleibt sichtbar).
  // Obergrenze: 15 % der Sample-Anzahl — bei sehr kurzen Trails (< 1 km)
  // wäre 80 m Smoothing > Track-Länge, das würde alles wegglätten. 15 %
  // ist konservativer als der alte 9 %-Wert (100 Samples → 15) und gibt
  // Mini-Trails noch genug Auflösung.
  const maxWindow = Math.max(3, Math.floor(sampleCount * 0.15));
  return Math.max(3, Math.min(maxWindow, idealWindow));
}

/**
 * Wave 14cf — Hilfsfunktion für den PMTiles-DEM-Pfad: erzeugt N Lat/Lon-
 * Sample-Positionen, gleichmäßig entlang der Track-Länge verteilt. Wird
 * an `sampleManyPmtilesElevations()` durchgereicht.
 */
function sampleTrackPositions(
  track: GpxTrack,
  count: number,
): Array<{ lat: number; lon: number }> {
  const points = track.points;
  const cum = cumulativeDistances(points);
  const total = cum[cum.length - 1] ?? 0;
  const out: Array<{ lat: number; lon: number }> = new Array(count);
  for (let s = 0; s < count; s++) {
    const targetDist = total > 0 ? (s / (count - 1)) * total : 0;
    let segI = 0;
    while (segI < cum.length - 1 && (cum[segI + 1] ?? 0) < targetDist) segI++;
    const a = points[segI]!;
    const b = points[Math.min(segI + 1, points.length - 1)]!;
    const dStart = cum[segI] ?? 0;
    const dEnd = cum[Math.min(segI + 1, cum.length - 1)] ?? dStart;
    const segLen = dEnd - dStart;
    const t = segLen > 0 ? (targetDist - dStart) / segLen : 0;
    out[s] = {
      lat: a.lat + (b.lat - a.lat) * t,
      lon: a.lon + (b.lon - a.lon) * t,
    };
  }
  return out;
}

/**
 * Wave 14cf — Validiert die Sample-Liste und füllt Lücken (null-Slots) mit
 * dem letzten validen Wert. Hardes Match zur `buildSrtmElevationSamples`-
 * Validierungs-Logik (≥30% valide, Spread ≥2m), damit der PMTiles-Pfad
 * dieselbe Quality-Bar erfüllt wie der MapLibre-Pfad.
 *
 * Returns null wenn die Validierung fehlschlägt — Caller fällt dann auf
 * den nächsten Pipeline-Stage.
 */
function fillNullsWithLastValid(
  rawSamples: Array<number | null>,
): number[] | null {
  const out = new Array<number>(rawSamples.length);
  let lastValid: number | null = null;
  let validCount = 0;
  let minE = Infinity;
  let maxE = -Infinity;
  for (let i = 0; i < rawSamples.length; i++) {
    const v = rawSamples[i];
    if (v != null && Number.isFinite(v)) {
      validCount++;
      lastValid = v;
      out[i] = v;
      if (v < minE) minE = v;
      if (v > maxE) maxE = v;
    } else {
      out[i] = lastValid ?? 0;
    }
  }
  if (validCount < Math.floor(rawSamples.length * 0.3)) {
    console.info(
      `[elev] PMTiles validation reject: only ${validCount}/${rawSamples.length} valid (<30%)`,
    );
    return null;
  }
  if (Number.isFinite(minE) && Number.isFinite(maxE) && maxE - minE < 2) {
    console.info(
      `[elev] PMTiles validation reject: spread ${(maxE - minE).toFixed(2)}m < 2m`,
    );
    return null;
  }
  return out;
}

/**
 * Downsample: aus N Track-Punkten mit ele → M Samples, gleichmäßig nach
 * Distanz. Wir suchen für jedes Sample den umgebenden Segment und
 * linear-interpolieren die Höhe.
 */
function downsampleGpxElevations(track: GpxTrack, count: number): number[] {
  const points = track.points;
  const cum = cumulativeDistances(points);
  const total = cum[cum.length - 1] ?? 0;
  const out = new Array<number>(count);

  for (let s = 0; s < count; s++) {
    const targetDist = (s / (count - 1)) * total;
    let segI = 0;
    while (segI < cum.length - 1 && (cum[segI + 1] ?? 0) < targetDist) segI++;
    const a = points[segI]!;
    const b = points[Math.min(segI + 1, points.length - 1)]!;
    const dStart = cum[segI] ?? 0;
    const dEnd = cum[Math.min(segI + 1, cum.length - 1)] ?? dStart;
    const segLen = dEnd - dStart;
    const t = segLen > 0 ? (targetDist - dStart) / segLen : 0;

    // Fallback: wenn ele fehlt, nimm den nächsten vorhandenen (selten, weil
    // hasElevationData() oben den ganzen Fall abfängt).
    const aEle = a.ele;
    const bEle = b.ele;
    if (aEle != null && bEle != null) {
      out[s] = aEle + (bEle - aEle) * t;
    } else if (aEle != null) {
      out[s] = aEle;
    } else if (bEle != null) {
      out[s] = bEle;
    } else {
      out[s] = 0;
    }
  }
  return out;
}

/**
 * Liest den gecachten SRTM-Samples-Eintrag für einen Trail.
 * Invalidiert Cache wenn:
 *  - Track-Länge mehr als 50m abweicht (Trail wurde neu importiert / editiert)
 *  - Höhen-Spread < 2m (schlechter Sample-Run auf teilweise geladenem DEM)
 *  - smoothingVersion fehlt oder abweicht (Wave 14bk: alte Roh-Samples
 *    werden verworfen damit nach Deploy alle Trails mit dem neuen
 *    Moving-Average neu geglättet werden)
 */
async function loadCachedSrtm(
  trailId: string,
  currentLengthMeters: number,
): Promise<number[] | null> {
  try {
    const db = await getDb();
    const entry = await db.get('trailElevations', trailId);
    if (!entry) return null;
    if (Math.abs(entry.trackLengthMeters - currentLengthMeters) > 50) {
      // Track-Länge hat sich merklich geändert → Cache stale, verwerfen.
      await db.delete('trailElevations', trailId);
      return null;
    }
    // Wave 14bk: Smoothing-Pipeline-Version prüfen. Cache-Einträge aus
    // früheren Builds (ohne das Feld) sind Roh-Samples ohne Smoothing und
    // würden als gezackte Profile gerendert. Verwerfen → nächster Fetch
    // läuft durch die Smooth-Pipeline.
    if (entry.smoothingVersion !== SMOOTHING_VERSION) {
      console.info(
        `[elev] Cache-Eintrag verworfen (smoothingVersion ${entry.smoothingVersion ?? 'undefined'} ≠ ${SMOOTHING_VERSION}):`,
        trailId,
      );
      await db.delete('trailElevations', trailId);
      return null;
    }
    // Wave 14aq: Sanity-Check gegen schlechte Cache-Einträge aus früheren
    // Wave-14-Builds wo `buildSrtmElevationSamples` bei nur teilweise
    // geladenen DEM-Tiles flat-gefüllte Arrays (alle 100 Samples denselben
    // Wert) als "valide" persistierte. Wenn wir jetzt so einen Eintrag
    // finden, verwerfen wir ihn — der nächste fresh-Fetch läuft mit der
    // neuen strikteren Validity-Hürde und kommt hoffentlich mit echtem
    // Profil zurück.
    let min = Infinity, max = -Infinity;
    for (const v of entry.samples) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (max - min < 2) {
      console.info('[elev] Cache-Eintrag verworfen (kein Höhen-Spread):', trailId);
      await db.delete('trailElevations', trailId);
      return null;
    }
    return entry.samples;
  } catch (err) {
    console.warn('[elev] loadCachedSrtm failed', err);
    return null;
  }
}

async function saveCachedSrtm(
  trailId: string,
  samples: number[],
  trackLengthMeters: number,
): Promise<void> {
  try {
    const db = await getDb();
    await db.put('trailElevations', {
      trailId,
      samples,
      source: 'srtm',
      computedAt: new Date().toISOString(),
      trackLengthMeters,
      smoothingVersion: SMOOTHING_VERSION,
    });
  } catch (err) {
    console.warn('[elev] saveCachedSrtm failed', err);
  }

  // Wave 14bq: Self-Healing-Push läuft jetzt durch elevationQueue statt
  // Fire-and-Forget. Grund: auf flakeligem Handy-Netz hat der alte Pfad
  // Pushes verloren — "wenn ich am handy ein höhenprofil erzeuge landet es
  // nicht IM BACKEND, also muss ich es am anderen gerät wieder erzeugen"
  // (User-Report). Die Queue parkt bei Fehler den Eintrag in IDB, der
  // nächste Mount/online-Event flushed ihn durch (analog Crux-Queue).
  //
  // Bewusst KEIN Debounce, weil saveCachedSrtm pro Trail max einmal pro
  // Sample-Run läuft (gate über loadCachedSrtm/smoothingVersion). Kein
  // Echo-Risiko.
  void enqueueElevationPush({
    trailId,
    samples,
    smoothingVersion: SMOOTHING_VERSION,
    trackLengthMeters,
  });
}

export interface UseTrailElevationInput {
  trailId: string | null;
  track: GpxTrack | null;
}

/**
 * Reagiert auf trailId-Änderungen. Wenn der Trail keine Höhen hat:
 *  - erst Cache checken (IndexedDB)
 *  - sonst via map.queryTerrainElevation() frisch sampeln + cachen
 *
 * Hinweis: queryTerrainElevation braucht geladene DEM-Tiles. Beim ersten
 * Aufruf sind die Tiles ggfs. noch nicht da — wir retryen daher nach
 * einem kurzen Delay (500ms) und nochmal nach 2s. Bei 3. Fehlversuch
 * geben wir auf und returnen source='none'.
 */
export function useTrailElevation(
  input: UseTrailElevationInput,
): TrailElevationData {
  // rebuild() wird hier erst später definiert, aber der initial-State braucht
  // schon ein Placeholder. Wir setzen es per setState nach unten.
  const [baseState, setBaseState] = useState<Omit<TrailElevationData, 'rebuild'>>({
    elevations: null,
    distances: [],
    totalMeters: 0,
    source: 'pending',
  });
  // Manuelle Cache-Bust-Version — jedes Inkrement triggert ein Re-Run des
  // Effects und überspringt den IDB-Cache (löscht ihn sogar), damit der
  // "Rebuild elevation"-Button wirklich fresh DEM-samples zieht.
  const [rebuildVersion, setRebuildVersion] = useState(0);
  // Gegen Race-Conditions bei schnellem Trail-Wechsel: jeder Effect-Run
  // bekommt eine ID, nur der neueste darf setState() callen.
  const runIdRef = useRef(0);
  // Den aktuellen trailId + track für die rebuild-Callback behalten, ohne neue
  // Referenz pro Render (useCallback + Ref-Pattern).
  const trailIdRef = useRef(input.trailId);
  trailIdRef.current = input.trailId;
  const trackRef = useRef(input.track);
  trackRef.current = input.track;
  // Wave 14as: Nach einem Rebuild panen wir die Kamera unsichtbar auf die
  // Trail-Bbox, um DEM-Tile-Loading für den gesamten Trail zu forcieren.
  // Diese Ref hält die Funktion zum Zurückpanen, die gecallt wird sobald
  // die Samples fertig sind (source raus aus 'pending').
  const restoreCameraRef = useRef<(() => void) | null>(null);

  const rebuild = useCallback(async () => {
    const tid = trailIdRef.current;
    const trk = trackRef.current;
    if (!tid) return;

    // Cache-Entry löschen damit der Hook nicht auf stale Samples fällt.
    try {
      const db = await getDb();
      await db.delete('trailElevations', tid);
    } catch (err) {
      console.warn('[elev] rebuild: cache delete failed (egal, Fetch lauft eh):', err);
    }

    // Wave 14as: DEM-Tile-Vorwärmung.
    //
    // queryTerrainElevation liest nur aus GELADENEN DEM-Tiles. Wenn der User
    // aktuell einen anderen Bereich der Map sieht (z.B. weit rausgezoomt oder
    // nur das eine Ende des Trails im Viewport), sind die Tiles für die Mitte
    // des Trails nicht gecacht → 70% der Samples werden null → Sampler fällt
    // unter 30%-Schwelle → null → Retry → "No data".
    //
    // Fix: kurz auf die Trail-Bbox fitBounds(animate:false), auf 'idle' warten
    // (Tiles geladen), dann läuft der normale Effect mit geladenen Tiles durch.
    // Nach dem Settle (source!=pending) pant der useEffect unten die Kamera
    // unauffällig zurück zu ihrer Ursprungs-Position.
    const map = mapBus.get();
    if (map && trk) {
      const bbox = trackBbox(trk.points);
      if (bbox) {
        const prevCam = {
          center: map.getCenter(),
          zoom: map.getZoom(),
          bearing: map.getBearing(),
          pitch: map.getPitch(),
        };
        try {
          map.fitBounds(bbox, { padding: 50, animate: false, duration: 0 });
          await waitForMapIdle(map, 3500);
        } catch (err) {
          console.warn('[elev] rebuild: fitBounds failed', err);
        }
        restoreCameraRef.current = () => {
          try {
            map.jumpTo(prevCam);
          } catch (err) {
            console.warn('[elev] rebuild: restore camera failed', err);
          }
        };
      }
    }

    setRebuildVersion((v) => v + 1);
  }, []);

  // Kamera zurücksetzen sobald die SRTM-Runde settled ist (source raus aus
  // 'pending'). 300ms Verzögerung damit der User visuell kurz mitbekommt dass
  // der Trail jetzt "framegepasst" war während Samples liefen.
  useEffect(() => {
    if (baseState.source === 'pending') return;
    const restore = restoreCameraRef.current;
    if (!restore) return;
    restoreCameraRef.current = null;
    const t = window.setTimeout(restore, 300);
    return () => window.clearTimeout(t);
  }, [baseState.source]);

  // Helfer, damit der Effect unten nicht bei jedem setBaseState auch rebuild
  // mitschleppen muss — die Rebuild-Referenz ist stabil dank useCallback.
  const setState = (s: Omit<TrailElevationData, 'rebuild'>) => setBaseState(s);

  useEffect(() => {
    runIdRef.current += 1;
    const myRunId = runIdRef.current;

    const { trailId, track } = input;
    if (!trailId || !track || track.points.length < 2) {
      setState({ elevations: null, distances: [], totalMeters: 0, source: 'none' });
      return;
    }

    const total = trackLength(track.points);
    // Wave 14cf-fix3: Sample-Count + Smoothing-Window jetzt distanzbasiert.
    // Helper gibt für 73 km z.B. ~1460 Samples + 2-Sample-Smoothing-Window.
    const sampleCount = pickSampleCount(total);
    const smoothingWindow = pickSmoothingWindow(total, sampleCount);
    const distances = sampleDistances(total, sampleCount);

    // Wave 14cc-fix4: GPX-direkter Pfad wurde DEMOTED.
    //
    // Vorher: wenn die GPX brauchbare Höhen hatte, haben wir die DIREKT
    // genommen und nichts anderes versucht. Das war richtig für Admin-
    // hochgeladene Strava-GPX (mit gut aufgezeichneten Bike-Computer-
    // Höhen), ABER tödlich für Custom Routes — die haben ihre Höhen
    // beim Route-Build aus den (in Liguria teilweise korrupten) PMTiles
    // gezogen und die falschen Werte sind ins GPX-File "eingebacken".
    //
    // Neuer User-Wunsch (14cc): "mach die berechnung immer online und
    // nur DANN offline, wenn das gerät offline ist". Wir vertrauen also
    // dem GPX-baked NICHT mehr als primäre Quelle. Online → HTTP frisch
    // ist immer first. GPX-baked bleibt als reiner Offline-Fallback in
    // der Pipeline (Admin-Trails kommen damit weiter zu ihren Strava-
    // Höhen offline, Custom Routes kriegen halt notgedrungen die
    // korrupten gebackenen Werte wenn offline).
    setState({ elevations: null, distances, totalMeters: total, source: 'pending' });

    let cancelled = false;
    let retryTimer: number | null = null;

    /**
     * Wave 14cc-fix4: Pipeline-Reorder — Online-First, Offline-Fallback.
     *
     * User-Wunsch: "mach die berechnung immer online und nur DANN offline,
     * wenn das gerät offline ist. davor vor ein paar versionen hat es
     * online geklappt".
     *
     * Hintergrund: in Wave 14cf wurde der PMTiles-direct-Sampler an die
     * Spitze gesetzt für Offline-by-Default-Performance. Das war richtig
     * für Korrektheit-by-Default-DEM-Annahme, ist aber falsch wenn die
     * lokalen DEM-Tiles in manchen Regionen (z. B. Aufstieg Melogno in
     * Liguria) korrupte Höhen-Daten haben. Online ist HTTP-eudem25m die
     * deutlich saubere Quelle.
     *
     * Neue Ordnung:
     *   1. IDB-Cache → instant-display wenn bereits gerechnet
     *   2. navigator.onLine === true → HTTP (opentopodata eudem25m) zuerst.
     *      Erfolg: persistieren + cachen → fertig.
     *   3. Offline (oder HTTP hat in Schritt 2 gefailed):
     *      a. PMTiles-direct (offline DEM aus OPFS)
     *      b. MapLibre queryTerrainElevation
     *      c. GPX-baked (admin-uploaded Strava-Höhen, oder Custom-Route-
     *         Höhen die zumindest IRGENDETWAS sind)
     *      d. Aufgeben → source='none'.
     */
    const runPipeline = async (): Promise<void> => {
      if (cancelled || runIdRef.current !== myRunId) return;

      // Wave 14dj: Easter-Egg "Everest" muss seinen hardcoded Gipfel von
      // 8850 m anzeigen — SRTM-HTTP-Sampler liefert für die Gipfel-
      // Koordinate sonst ~8685 m (bekannte SRTM-Gipfel-Unterschätzung)
      // und macht den Joke kaputt. GPX-Werte direkt durchschleifen und
      // alle DEM-Pfade überspringen.
      //
      // Wave 14dp: zusätzlich den IDB-Cache für diese trailId AKTIV
      // wegputzen — sonst zeigt eine alte SRTM-Sample-Reihe von vor dem
      // Bypass-Fix dauerhaft 8685, selbst wenn der Bypass danach läuft
      // (ein vorheriger Render hatte die cached-Variante schon gerendert,
      // React behält dann den State zurück). + console.info damit der
      // User in Safari-Devtools sehen kann ob der Bypass-Pfad triggert.
      if (isEverestEasterEggId(trailId) && hasElevationData(track)) {
        console.info(
          '[elev] Everest easter-egg: bypassing DEM/SRTM, using hardcoded GPX waypoints (summit=8850m)',
        );
        // Stale IDB-Eintrag löschen falls vorhanden — fire-and-forget,
        // blockt nicht das Setzen der frischen Samples.
        void (async () => {
          try {
            const db = await getDb();
            await db.delete('trailElevations', trailId);
          } catch {
            /* IDB nicht verfügbar oder kein Eintrag — egal, Bypass läuft eh */
          }
        })();
        const raw = downsampleGpxElevations(track, sampleCount);
        setState({
          elevations: raw,
          distances,
          totalMeters: total,
          source: 'gpx',
        });
        return;
      }

      // 1. IDB-Cache — instant-display, egal ob online/offline.
      const cached = await loadCachedSrtm(trailId, total);
      if (cancelled || runIdRef.current !== myRunId) return;
      if (cached) {
        // Wave 14cf-fix3: Cache kann mit anderer Sample-Count gespeichert
        // worden sein (Legacy 100, oder spätere Tweaks). Distances aus
        // ECHTER Sample-Länge ableiten — sonst Mismatch zwischen
        // distances.length (z.B. 1460) und elevations.length (z.B. 100)
        // → ElevationProfile rendert chaotisch.
        const cachedDistances =
          cached.length === distances.length
            ? distances
            : sampleDistances(total, cached.length);
        setState({
          elevations: cached,
          distances: cachedDistances,
          totalMeters: total,
          source: 'srtm',
        });
        return;
      }

      // Helper: shared persist-Funktion mit der 3-Stage-Cleanup-Pipeline.
      // Wave 14cc-fix3: Grade-Clamp → Hampel-Despike → Smooth.
      const persist = (raw: number[], source: 'srtm' | 'gpx') => {
        const stepM = total / Math.max(1, sampleCount - 1);
        const clamped = clampElevationByGrade(raw, stepM);
        const cleaned = despikeElevationSamples(clamped);
        const samples = smoothElevationSamples(cleaned, smoothingWindow);
        // Wave 14dq: Paranoid-Clamp für Everest-Easter-Egg. Selbst wenn
        // irgendwie ein SRTM-Pfad durch den Bypass am Anfang von runPipeline
        // schlüpfen würde (Cache-Race, alter Bundle, was auch immer), force
        // ich hier das letzte Sample auf 8850. User-Wunsch: 'da MUSS 8850
        // stehen, nicht 8685, worst case GPX modifizieren'.
        if (isEverestEasterEggId(trailId) && samples.length > 0) {
          samples[samples.length - 1] = 8850;
        }
        setState({
          elevations: samples,
          distances,
          totalMeters: total,
          source,
        });
        void saveCachedSrtm(trailId, samples, total);
      };

      // 2. ONLINE-FIRST: HTTP (opentopodata eudem25m) als primärer
      //    Korrektheits-Pfad. EU-DEM 25 m ist eine andere Datenquelle als
      //    unsere lokalen Liguria-PMTiles und hat den Melogno-Korruptions-
      //    Bug nicht. Wenn der Request klappt, cachen wir → nächster Aufruf
      //    geht via Schritt 1 (cache) ohne erneuten Roundtrip.
      const isOnline =
        typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
      if (isOnline) {
        try {
          const httpRaw = await fetchHttpElevations(track, sampleCount);
          if (cancelled || runIdRef.current !== myRunId) return;
          if (httpRaw) {
            console.info(
              `[elev] HTTP (eudem25m) sampler: ${trailId} → ${httpRaw.length} samples`,
            );
            persist(httpRaw, 'srtm');
            return;
          }
          console.info(
            `[elev] HTTP returned no samples for ${trailId} — falling back to offline DEM`,
          );
        } catch (err) {
          console.warn(
            `[elev] HTTP sampler failed for ${trailId}, falling back to offline DEM:`,
            err,
          );
        }
      } else {
        console.info(
          `[elev] navigator.onLine=false — skipping HTTP, going straight to offline DEM`,
        );
      }

      // 3a. PMTiles-direct (offline DEM aus OPFS).
      if (isOfflineDemAvailable()) {
        try {
          const positions = sampleTrackPositions(track, sampleCount);
          const rawSamples = await sampleManyPmtilesElevations(positions);
          if (cancelled || runIdRef.current !== myRunId) return;
          const filled = fillNullsWithLastValid(rawSamples);
          if (filled) {
            console.info(
              `[elev] PMTiles-direct sampler: ${trailId} → ${filled.length} samples`,
            );
            persist(filled, 'srtm');
            return;
          }
        } catch (err) {
          console.warn(
            `[elev] PMTiles-direct sampler failed for ${trailId}, falling through:`,
            err,
          );
        }
      }

      // 3b. MapLibre queryTerrainElevation — nur wenn Map-Viewport-Tiles
      //     gerade geladen sind. Last-ditch offline-Pfad bevor wir GPX-
      //     baked nehmen müssen.
      const map = mapBus.get();
      if (map) {
        const trySample = async (): Promise<number[] | null> => {
          const raw = await buildSrtmElevationSamples(track, map, sampleCount);
          if (cancelled || runIdRef.current !== myRunId) return null;
          return raw;
        };
        const first = await trySample();
        if (cancelled || runIdRef.current !== myRunId) return;
        if (first) {
          persist(first, 'srtm');
          return;
        }
        if (rebuildVersion === 0) {
          const idleTimeout = 2000;
          await new Promise<void>((resolve) => {
            let done = false;
            const finish = () => {
              if (done) return;
              done = true;
              try {
                map.off('idle', finish);
              } catch {
                /* noop */
              }
              resolve();
            };
            map.on('idle', finish);
            retryTimer = window.setTimeout(finish, idleTimeout);
          });
          if (cancelled || runIdRef.current !== myRunId) return;
          const second = await trySample();
          if (cancelled || runIdRef.current !== myRunId) return;
          if (second) {
            persist(second, 'srtm');
            return;
          }
        }
      }

      // 3c. GPX-baked als letzte Reserve.
      //
      // Vor 14cc-fix4 war das der ERSTE Pfad (rebuildVersion=0 &&
      // hasElevationData). Jetzt nur als Notnagel: wenn Online-HTTP
      // und alle Offline-DEMs failed haben (z. B. tief im Wald ohne Netz,
      // kein PMTiles für die Region, keine viewport-Tiles geladen) ist
      // GPX-baked immer noch besser als gar nichts. Für Custom Routes
      // mit korrupten Werten ist das nicht ideal — aber dann ist man
      // offline und kann es eh nicht besser tun.
      if (hasElevationData(track)) {
        const raw = downsampleGpxElevations(track, sampleCount);
        console.info(
          `[elev] GPX-baked elevation (offline last-resort) for ${trailId}`,
        );
        persist(raw, 'gpx');
        return;
      }

      // 4. Aufgeben.
      setState({ elevations: null, distances, totalMeters: total, source: 'none' });
    };

    void runPipeline();

    return () => {
      cancelled = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
    };
  }, [input.trailId, input.track, rebuildVersion]);

  return { ...baseState, rebuild };
}
